import {
  ActionIcon,
  Button,
  Group,
  Menu,
  Tooltip,
  useMantineTheme,
} from '@mantine/core'
import { openModal } from '@mantine/modals'
import { showNotification } from '@mantine/notifications'
import { useMutation } from '@tanstack/react-query'
import { TDashboard } from 'api/dashboards/getDashboard'
import { setDefaultDeviceDashboard } from 'api/dashboards/deviceDashboards/setAsDefaultDeviceDashboard'
import { setDefaultDeviceModelDashboard } from 'api/dashboards/deviceModelDashboards/setAsDefaultDeviceModelDashboard'
import { AxiosError } from 'axios'
import { useDeviceModelContext } from 'components/devices/DeviceIndicators'
import { CSSProperties, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { BiCustomize } from 'react-icons/bi'
import { IoClose } from 'react-icons/io5'
import { TbCheckbox, TbChevronDown, TbEdit, TbTrash } from 'react-icons/tb'
import { useParams } from 'react-router-dom'
import { ErrorResponse } from 'shared/types'
import { DashboardCopy } from './DashboardCopy'
import { DashboardCreate } from './DashboardCreate'
import { useDashboardContext } from './DashboardProvider'
import { EditDashboardInfoForm } from './EditDashboardInfo'
import {
  useDashboardActions,
  useDashboardStore,
} from './hooks/useDashboardStore'
import { useRemoveDashboard } from './hooks/useRemoveDashboard'
import classes from './DashboardCustomizer.module.css'

export const DashboardCustomizer = ({ onChange }: { onChange: () => void }) => {
  const dashboard = useDashboardContext()
  const { toggleEditMode } = useDashboardActions()
  const isEditMode = useDashboardStore(state => state.isEditMode)
  const { t } = useTranslation()

  const onToggle = () => {
    if (isEditMode) {
      showNotification({
        color: 'blue',
        message: 'Dashboard saved',
      })
    }
    toggleEditMode()
  }

  const theme = useMantineTheme()
  const menuControlStyle = {
    '--dashboard-customizer-separator-color':
      theme.colorScheme === 'dark' ? theme.colors.dark[7] : theme.white,
  } as CSSProperties

  const Icon = isEditMode ? IoClose : BiCustomize

  useEffect(() => {
    return () => {
      if (isEditMode) toggleEditMode(false)
    }
  }, [isEditMode])

  return (
    <Group wrap="nowrap" gap={0}>
      <Tooltip
        label={
          isEditMode
            ? t('dashboard.exitEditMode')
            : t('dashboard.enterEditMode')
        }
      >
        <Button onClick={onToggle} className={classes.button}>
          <Icon size="1.2rem" />
        </Button>
      </Tooltip>
      <Menu
        transitionProps={{
          transition: 'pop',
        }}
        position="bottom-end"
        withinPortal
        disabled={isEditMode}
      >
        <Menu.Target>
          <ActionIcon
            variant="filled"
            color={theme.primaryColor}
            size={36}
            className={classes.menuControl}
            style={menuControlStyle}
            disabled={isEditMode}
          >
            <TbChevronDown size="1rem" />
          </ActionIcon>
        </Menu.Target>
        <Menu.Dropdown>
          <DashboardCreate />
          <DashboardCopy dashboardId={dashboard.id} />
          <EditDashboard />
          <SetAsDefault />
          <DeleteDashboard onDelete={onChange} {...dashboard} />
        </Menu.Dropdown>
      </Menu>
    </Group>
  )
}

const DeleteDashboard = ({
  id,
  type,
  onDelete,
}: TDashboard & { onDelete: () => void }) => {
  const { t } = useTranslation()

  const openModal = useRemoveDashboard({ id }, onDelete)

  return (
    <Menu.Item onClick={openModal} c={'red'} leftSection={<TbTrash />}>
      {t('generic.delete')}
    </Menu.Item>
  )
}

const SetAsDefault = () => {
  const { t } = useTranslation()
  const dashboard = useDashboardContext()
  const deviceModelId = useDeviceModelContext()
  const { deviceId } = useParams()
  const mutation = useMutation({
    mutationFn: ({ dashboardId }: { dashboardId: string }) => {
      if (dashboard.type === 'MODEL') {
        if (!deviceModelId) {
          return Promise.reject(new Error('Missing device model id'))
        }
        return setDefaultDeviceModelDashboard({ deviceModelId, dashboardId })
      }

      if (!deviceId) {
        return Promise.reject(new Error('Missing device id'))
      }
      return setDefaultDeviceDashboard({ deviceId, dashboardId })
    },
    onSuccess: () => {
      showNotification({
        color: 'green',
        message: t('dashboard.defaultSetSuccess'),
      })
    },
    onError: (data: AxiosError<ErrorResponse>) => {
      showNotification({
        color: 'red',
        message:
          data.response?.data.errors.map(error => error.message).join(', ') ??
          t('generic.errorOccured'),
      })
    },
  })

  return (
    <Menu.Item
      onClick={() => mutation.mutate({ dashboardId: dashboard.id })}
      leftSection={<TbCheckbox />}
    >
      Ustaw jako domyślny
    </Menu.Item>
  )
}

const EditDashboard = () => {
  const dashboard = useDashboardContext()
  const { t } = useTranslation()
  const onClick = () => {
    openModal({
      title: t('dashboard.edit'),
      children: (
        <EditDashboardInfoForm
          id={dashboard.id}
          initial={{ name: dashboard.name }}
        />
      ),
    })
  }

  return (
    <Menu.Item onClick={onClick} leftSection={<TbEdit />}>
      {t('generic.edit')}
    </Menu.Item>
  )
}
